'use strict';

define('vb/action/builtin/forEachAction',['vb/action/action', 'vb/private/constants', 'vb/private/action/actionHelper'],
  (Action, Constants, ActionHelper) => {
    /** @typedef {import('vb/action/action').Outcome} Outcome */

    const MODES = {
      SERIAL: 'serial',
      PARALLEL: 'parallel',
    };

    const VALID_MODES = Object.values(MODES);

    /**
     * Represents an action that performs a "for-each" operation on an array of items.
     *
     * @extends Action
     * @category Action
     */
    class ForEachAction extends Action {
      /**
       * Performs the For Each action on the provided parameters.
       *
       * @param {Object} parameters - The input parameters for the action.
       * @param {Array} parameters.items - The array of items to iterate over.
       * @param {string} parameters.actionId - The ID of the action to execute for each item.
       * @param {string} [parameters.as] - An optional alias for the `$current` variable.
       * @param {string} [parameters.mode='serial'] - The execution mode, either 'serial' or 'parallel'.
       *
       * @returns {Promise<Outcome>} A promise resolving to the outcome of the action.
       */
      perform(parameters) {
        return Promise.resolve()
          .then(() => {
            const {
              items,
              mode,
              actionId,
            } = parameters;

            // this is a failure of the runtime, not the app
            if (!this.actionChain || !this.availableContexts) {
              throw new Error(`ForEachAction (${this.logLabel}): invalid execution context, internal error`);
            }

            // VBS-46961: Adding a message caused CX_SVC-SERVICE-CENTER-SP to fail in RFR
            if (!Array.isArray(items)) {
              this.log.error('ForEachAction', this.logLabel, '\'items\' is not an array');
              return Action.createFailureOutcome(/* 'The items parameters is not an array.' */);
            }
            if (typeof actionId !== 'string') {
              this.log.error('ForEachAction', this.logLabel, 'invalid actionId', actionId);
              return Action.createFailureOutcome(/* `Invalid actionId ${actionId}` */);
            }
            if (mode && VALID_MODES.indexOf(mode) === -1) {
              this.log.error('ForEachAction', this.logLabel, 'invalid mode value', mode,
                'must be one of', JSON.stringify(VALID_MODES));
              return Action.createFailureOutcome(/* `Invalid mode ${mode}` */);
            }

            // set this, before calling createNewContext
            this.alias = parameters.as;

            const promises = [];

            let promise = Promise.resolve(); // initial promise
            items.forEach((data, index) => {
              // the context for the Callee (called) action - need a new one for each iteration
              const actionContext = ActionHelper.createNewContext(this.availableContexts, this);

              // if we are NOT parallel, we need to wait for the previous promise to resolve/reject
              const promiseToWaitFor = (mode === MODES.PARALLEL) ? Promise.resolve() : promise;

              promise = promiseToWaitFor.then(() => {
                // $current may be replaced by sub-actions, but the alias will not
                actionContext[Constants.ContextName.CURRENT] = {
                  data,
                  index,
                };
                return this.actionChain.runActionStep(actionId, actionContext);
              });

              promises.push(promise);

              // don't reject any promises; just put the error in the results
              promise
                .catch((e) => {
                  this.log.error('ForEachAction', this.logLabel, e);
                  return e; // todo: ok to have the Error (or whatever) in the resolved results?
                });
            });

            // wait for all promises, before completing action
            return Promise.all(promises)
              .then((results) => Action.createSuccessOutcome(results))
              // this catch() should not happen, since we convert each promise to success (see 'don't reject')
              .catch((error) => Action.createFailureOutcome(error));
          });
      }

      /**
       * Sets the context for the ForEachAction instance.
       *
       * This method is typically called by the action chain and provides the necessary dependencies for the action
       * to operate.
       *
       * @param {object} actionChain - The action chain that owns this ForEachAction instance.
       * @param {object} availableContexts - The available contexts for the action to use.
       */
      setContext(actionChain, availableContexts) {
        this.actionChain = actionChain;
        this.availableContexts = availableContexts;
      }
    }

    return ForEachAction;
  });

